import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SocketScannerDevice, SocketScannerPluginProxy } from './socket-scanner-plugin.proxy';
import { LogService } from '../logger/log.service';
import { filter, map } from 'rxjs/operators';
import { ScannerResultAction } from '@core/capacitor-plugins/capacitor-plugins.class';
import { TargetDeviceEnum } from '@shared/constants';
import { PosInfoService } from '@core/services/system/pos-info.service';

type SubjectType = { action: ScannerResultAction; value: SocketScannerDevice | string };
type ScannerLifecycleState = 'stopped' | 'starting' | 'running' | 'stopping';

@Injectable()
export class SocketScannerService {
  connectedDevices: SocketScannerDevice[] = [];
  private subject = new Subject<SubjectType>();
  private readonly logger = this.logService.createLogger('SocketScannerService');
  private lifecycleState: ScannerLifecycleState = 'stopped';
  private listenerGeneration = 0;
  private startPromise: Promise<void> | null = null;
  private stopPromise: Promise<void> | null = null;

  constructor(private logService: LogService, private pluginProxy: SocketScannerPluginProxy, private posInfoService: PosInfoService) {}

  startObserve(): Promise<void> {
    if (this.lifecycleState === 'running') {
      this.logger.debug('startObserve skipped: scanner already running');
      return Promise.resolve();
    }

    if (this.lifecycleState === 'starting') {
      this.logger.debug('startObserve skipped: scanner start already in progress');
      return this.startPromise ?? Promise.resolve();
    }

    if (this.lifecycleState === 'stopping') {
      if (!this.startPromise) {
        this.logger.debug('startObserve waiting for scanner stop in progress');
        const pendingStartPromise = (this.stopPromise ?? Promise.resolve()).then(() => this.createStartPromise());
        const cleanupPendingStartPromise = () => {
          if (this.startPromise === pendingStartPromise) {
            this.startPromise = null;
          }
        };
        this.startPromise = pendingStartPromise;
        pendingStartPromise.then(cleanupPendingStartPromise, cleanupPendingStartPromise);
      }
      return this.startPromise;
    }

    return this.createStartPromise();
  }

  stopObserve(): Promise<void> {
    if (this.lifecycleState === 'stopped') {
      this.logger.debug('stopObserve skipped: scanner already stopped');
      this.onStopObserve();
      return Promise.resolve();
    }

    if (this.lifecycleState === 'stopping') {
      this.logger.debug('stopObserve skipped: scanner stop already in progress');
      return this.stopPromise ?? Promise.resolve();
    }

    const generation = ++this.listenerGeneration;
    this.lifecycleState = 'stopping';

    this.stopPromise = Promise.resolve(this.startPromise)
      .then(() => this.pluginProxy.disconnect())
      .then(() => {
        this.onStopObserve();
        this.logger.debug('stopObserve');
      })
      .catch((error) => {
        this.onStopObserve();
        this.logger.error(error, 'stopObserve');
        throw error;
      })
      .finally(() => {
        if (this.isCurrentGeneration(generation)) {
          this.lifecycleState = 'stopped';
        }
        if (this.stopPromise) {
          this.stopPromise = null;
        }
      });

    return this.stopPromise;
  }

  onConnected(): Observable<SocketScannerDevice> {
    return this.subject.asObservable().pipe(
      filter((item) => item.action === ScannerResultAction.deviceConnected),
      map((item) => item.value as SocketScannerDevice)
    );
  }

  onDisconnected(): Observable<string> {
    return this.subject.asObservable().pipe(
      filter((item) => item.action === ScannerResultAction.deviceDisconnected),
      map((item) => item.value as string)
    );
  }

  onData(): Observable<string> {
    return this.subject.asObservable().pipe(
      filter((item) => item.action === ScannerResultAction.dataReceived),
      map((item) => item.value as string)
    );
  }

  private onStopObserve() {
    this.connectedDevices = this.connectedDevices.filter((device) => {
      this.subject.next({
        action: ScannerResultAction.deviceDisconnected,
        value: device.guid,
      });
      return false;
    });
  }

  private addScannerToList(scanner: SocketScannerDevice) {
    const currentScanner = this.connectedDevices.find((item) => item.guid === scanner.guid);
    if (currentScanner) {
      currentScanner.batteryLevel = scanner.batteryLevel;
      return;
    }
    this.connectedDevices = [...this.connectedDevices, scanner];
  }

  private createStartPromise(): Promise<void> {
    const generation = ++this.listenerGeneration;
    this.lifecycleState = 'starting';

    const promise = this.startObserveInternal(generation)
      .catch((error) => {
        if (this.isCurrentGeneration(generation)) {
          this.lifecycleState = 'stopped';
        }
        this.logger.error(error, 'startObserve');
        throw error;
      })
      .finally(() => {
        if (this.startPromise === promise) {
          this.startPromise = null;
        }
      });

    this.startPromise = promise;
    return promise;
  }

  private async startObserveInternal(generation: number): Promise<void> {
    const targetDeviceEnum: TargetDeviceEnum = await this.posInfoService.detectedTargetDeviceModel();

    if (targetDeviceEnum === TargetDeviceEnum.ADYEN) {
      this.logger.info('Device not support socket scanner');
      if (this.isCurrentGeneration(generation)) {
        this.lifecycleState = 'stopped';
      }
      return;
    }

    this.logger.debug('startObserve');

    await this.pluginProxy.connect(
      (result) => {
        if (!this.isCurrentGeneration(generation)) {
          return;
        }
        this.logger.debug('startObserve onDeviceConnected ', { result });
        if (result.guid) {
          this.addScannerToList(result);
        }

        this.subject.next({
          action: ScannerResultAction.deviceConnected,
          value: result,
        });
      },
      (guid) => {
        if (!this.isCurrentGeneration(generation)) {
          return;
        }
        this.logger.debug('startObserve onDeviceDisconnected ', { guid });
        if (guid) {
          this.connectedDevices = this.connectedDevices.filter((value) => value.guid !== guid);
        }

        this.subject.next({
          action: ScannerResultAction.deviceDisconnected,
          value: guid,
        });
      },
      (data) => {
        if (!this.isCurrentGeneration(generation)) {
          return;
        }
        this.logger.debug('startObserve onDataReceived ', { data });
        this.subject.next({
          action: ScannerResultAction.dataReceived,
          value: data,
        });
      },
      (errorCode) => {
        if (!this.isCurrentGeneration(generation)) {
          return;
        }
        this.logger.error(errorCode, 'startObserve');
      }
    );

    if (this.isCurrentGeneration(generation)) {
      this.lifecycleState = 'running';
    }
  }

  private isCurrentGeneration(generation: number): boolean {
    return this.listenerGeneration === generation;
  }
}
